/**
 * useApprovalsCount (SYN-1521) — the sidebar Approvals badge count over
 * `GET /api/workspace/approvals/count` (the unified inbox's cheap pending
 * tally: access requests + run confirmations + escalations).
 *
 * Admin-only data (the BFF 403s members), so callers pass `enabled` from the
 * same org-admin gate that shows the Workspace nav — members never fetch.
 * Counts refresh on APPROVALS_CHANGED_EVENT (raised by useApprovals after
 * every refetch/resolution) so the badge tracks the inbox without polling.
 * Fail-closed: any error renders no badge rather than a stale number.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { APPROVALS_CHANGED_EVENT } from "./useApprovals";

interface ApprovalsCountResponse {
  total?: number;
}

export function useApprovalsCount(enabled: boolean): number | null {
  const [count, setCount] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<ApprovalsCountResponse>("/api/workspace/approvals/count");
      const total = data && typeof data.total === "number" && data.total >= 0 ? data.total : null;
      setCount(total);
    } catch {
      setCount(null);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCount(null);
      return;
    }
    void load();
    const onChange = (): void => {
      void load();
    };
    window.addEventListener(APPROVALS_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener(APPROVALS_CHANGED_EVENT, onChange);
    };
  }, [enabled, load]);

  return count;
}
